import { NpTestPlanInformant, NpTestPlanMeasure, NpTestPlanMeasureType } from './npTestPlanTypes'

export const NP_TEST_PLAN_MEASURE_TYPE_ORDER: NpTestPlanMeasureType[] = [
  NpTestPlanMeasureType.RATING_SCALE,
  NpTestPlanMeasureType.F2F,
]

export const NP_TEST_PLAN_INFORMANT_ORDER: NpTestPlanInformant[] = [
  NpTestPlanInformant.SELF_REPORT,
  NpTestPlanInformant.CAREGIVER,
  NpTestPlanInformant.TEACHER,
  NpTestPlanInformant.THERAPIST,
  NpTestPlanInformant.OTHER,
]

export interface NpTestPlanMeasureGroup {
  informant: NpTestPlanInformant
  measures: NpTestPlanMeasure[]
}

export interface NpTestPlanMeasureSection {
  measureType: NpTestPlanMeasureType
  groups: NpTestPlanMeasureGroup[]
}

export interface NpTestPlanDuration {
  min: number
  max: number | null
}

/** `includeEmpty` keeps every section and informant, so the plan can show a card for each scope. */
export const groupMeasures = (
  measures: NpTestPlanMeasure[],
  { includeEmpty = false }: { includeEmpty?: boolean } = {},
): NpTestPlanMeasureSection[] =>
  NP_TEST_PLAN_MEASURE_TYPE_ORDER.reduce<NpTestPlanMeasureSection[]>((sections, measureType) => {
    const ofType = measures.filter((measure) => measure.measureType === measureType)
    if (!ofType.length && !includeEmpty) {
      return sections
    }

    const groups = NP_TEST_PLAN_INFORMANT_ORDER.reduce<NpTestPlanMeasureGroup[]>((acc, informant) => {
      const ofInformant = ofType.filter((measure) => measure.informant === informant)
      return ofInformant.length || includeEmpty ? [...acc, { informant, measures: ofInformant }] : acc
    }, [])

    return [...sections, { measureType, groups }]
  }, [])

export interface NpTestPlanCategoryBand {
  category: string
  measures: NpTestPlanMeasure[]
}

export const groupByCategory = (measures: NpTestPlanMeasure[]): NpTestPlanCategoryBand[] =>
  measures.reduce<NpTestPlanCategoryBand[]>((bands, measure) => {
    const existing = bands.find(({ category }) => category === measure.category)
    if (existing) {
      return bands.map((band) => (band === existing ? { ...band, measures: [...band.measures, measure] } : band))
    }
    return [...bands, { category: measure.category, measures: [measure] }]
  }, [])

export interface NpTestPlanCatalogFilters {
  measureType: NpTestPlanMeasureType
  /** Null offers every informant. */
  informant: NpTestPlanInformant | null
  /** Null offers every age. */
  ageRange: NpTestPlanAgeRange | null
  search: string
  planIds: Set<string>
}

export interface NpTestPlanAgeRange {
  min: number
  max: number
}

export const NP_TEST_PLAN_OLDEST_AGE_FILTER = 26

const isAgeAppropriate = (measure: NpTestPlanMeasure, ageRange: NpTestPlanAgeRange | null): boolean =>
  ageRange === null || (measure.minAge <= ageRange.max && measure.maxAge >= ageRange.min)

// Plan measures skip the age filter, since a battery can seed measures the member has aged past.
export const filterCatalog = (
  measures: NpTestPlanMeasure[],
  { measureType, informant, ageRange, search, planIds }: NpTestPlanCatalogFilters,
): NpTestPlanMeasure[] => {
  const query = search.trim().toLowerCase()

  return measures.filter(
    (measure) =>
      measure.measureType === measureType &&
      (!informant || measure.informant === informant) &&
      (planIds.has(measure.id) || isAgeAppropriate(measure, ageRange)) &&
      (!query || measure.name.toLowerCase().includes(query)),
  )
}

export const NP_TEST_PLAN_UNBOUNDED_AGE = 120

// Unpicked, the member's exact age filters; picking the oldest option means that age and up.
export const toAgeRange = (
  patientAge: number | null,
  pickedAge: number | null | undefined,
): NpTestPlanAgeRange | null => {
  const age = pickedAge === undefined ? patientAge : pickedAge
  if (age === null) {
    return null
  }
  return { min: age, max: pickedAge === NP_TEST_PLAN_OLDEST_AGE_FILTER ? NP_TEST_PLAN_UNBOUNDED_AGE : age }
}

export const getAgeRange = (measure: NpTestPlanMeasure): { min: number; max: number | null } => ({
  min: Math.round(measure.minAge),
  max: measure.maxAge >= NP_TEST_PLAN_UNBOUNDED_AGE ? null : Math.round(measure.maxAge),
})

export const sumDuration = (measures: NpTestPlanMeasure[]): NpTestPlanDuration => {
  const min = measures.reduce((total, measure) => total + measure.estimatedTimeMin, 0)
  const max = measures.reduce((total, measure) => total + measure.estimatedTimeMax, 0)
  return { min, max: max === min ? null : max }
}
